import { useState } from 'react'
import dayjs from 'dayjs'
import {
  Drawer,
  DrawerContent,
  DrawerHeader,
  DrawerTitle,
} from '@/components/ui/drawer'
import { Card } from '@/components/ui/card'
import { AlertBadge } from '@/components/ui/alert-badge'
import { DetailSkeleton } from '@/components/ui/skeleton-presets'
import { EmptyState } from '@/components/EmptyState'
import { TriangleAlert, X } from '@/components/icons'
import { FlightLogDrawer } from '@/components/FlightLogDrawer'
import {
  useFlightLogDefectsToReview,
  useFlightLogMarkDefectsViewed,
} from '@/lib/hooks'

interface DefectsLoggedDrawerProps {
  open: boolean
  onOpenChange: (open: boolean) => void
}

// Lean row shape of flightLog.getDefectsToReview (FlightLogListItem on the
// API side — the service file isn't importable from the app, so mirror it).
interface DefectLogItem {
  id: string
  tripLegId: string
  logNumber: number
  departureAirportIcao: string
  arrivalAirportIcao: string
  departureDateTime: string | Date
  submittedAt: string | Date | null
  defectsViewedAt: string | Date | null
  defectSummary: string | null
}

/**
 * Admin review queue for deferred defects (Figma 12387:42228): submitted
 * flight logs carrying a Deferred (MEL) defect. Opening a log marks it viewed
 * and shows the read-only technical log jumped straight to the Defects
 * section; viewed logs stay listed with a checked badge.
 */
export function DefectsLoggedDrawer({ open, onOpenChange }: DefectsLoggedDrawerProps) {
  const { data: logs, isLoading } = useFlightLogDefectsToReview(open)
  const markViewed = useFlightLogMarkDefectsViewed()
  // Id sticks around after close so the drawer can animate out
  const [selectedLegId, setSelectedLegId] = useState<string | null>(null)
  const [isLogOpen, setIsLogOpen] = useState(false)

  return (
    <>
      <Drawer open={open} onOpenChange={onOpenChange}>
        <DrawerContent className="bg-surface-L0 max-h-[85dvh] flex flex-col">
          <DrawerHeader className="flex items-center justify-between p-4">
            <DrawerTitle className="text-lg font-semibold text-content-primary-bold">
              Defects Logged
            </DrawerTitle>
            <button
              onClick={() => onOpenChange(false)}
              className="size-8 rounded-full bg-surface-L2 flex items-center justify-center hover:bg-surface-L2/80 transition-colors shrink-0"
              aria-label="Close"
            >
              <X className="size-4 text-content-primary-subtle" />
            </button>
          </DrawerHeader>

          <div
            className="min-h-0 flex-1 overflow-y-auto overscroll-contain px-4 pb-4"
            style={{ WebkitOverflowScrolling: 'touch' }}
          >
            {isLoading ? (
              <DetailSkeleton rows={4} />
            ) : !logs?.length ? (
              <EmptyState
                icon={TriangleAlert}
                title="No defects to review"
                description="Deferred defects from submitted flight logs will appear here."
              />
            ) : (
              <div className="flex flex-col gap-2">
                {(logs as DefectLogItem[]).map((log) => {
                  const date = log.submittedAt ?? log.departureDateTime
                  return (
                    <Card
                      key={log.id}
                      interactive
                      onClick={() => {
                        markViewed.mutate({ flightLogId: log.id })
                        setSelectedLegId(log.tripLegId)
                        setIsLogOpen(true)
                      }}
                      className="flex flex-col gap-2 rounded-2xl border border-border-L1 bg-surface-L0 px-4 py-2 shadow-[0px_1px_3.5px_rgba(0,0,0,0.05)]"
                    >
                      <div className="flex items-center justify-between gap-2">
                        <p className="flex min-w-0 items-center gap-[7px] truncate text-sm font-semibold leading-5">
                          <span className="text-content-primary-bold">{log.logNumber}</span>
                          <span className="truncate text-content-primary-subtle">
                            {log.departureAirportIcao} → {log.arrivalAirportIcao}
                          </span>
                          <span className="text-xs text-content-primary-subtle">•</span>
                          <span className="text-content-primary-subtle">
                            {dayjs.utc(date).format('D MMM')}
                          </span>
                        </p>
                        <AlertBadge variant={log.defectsViewedAt ? 'valid' : 'error'} />
                      </div>
                      <div className="flex items-center gap-2">
                        <span className="flex min-h-6 shrink-0 items-center rounded-full bg-background-negative-subtle px-2 text-xs font-semibold text-content-negative-bold">
                          Defect Deferred
                        </span>
                        {log.defectSummary && (
                          <span className="min-w-0 truncate text-sm font-medium tracking-[0.07px] text-content-primary-bold">
                            {log.defectSummary}
                          </span>
                        )}
                      </div>
                    </Card>
                  )
                })}
              </div>
            )}
          </div>
        </DrawerContent>
      </Drawer>

      {selectedLegId && (
        <FlightLogDrawer
          isOpen={isLogOpen}
          onClose={() => setIsLogOpen(false)}
          tripLegId={selectedLegId}
          readOnly
          initialSection="defects"
        />
      )}
    </>
  )
}
